import { useApiClient } from '~/api-client';
import { UnitsTabResource } from '@mark43/cad-api';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { ResponsePlanGeneratedMessage } from '../../socket/stream-types';
import { createStreamListener, useStream } from '../../socket/use-stream';
import { TAB_QUERY_CACHE_TIME } from '../tabs/constants';

export function useEventUnitsTabQuery(props: { eventId?: number }) {
    const { eventId } = props;
    const apiClient = useApiClient();
    const { asyncResponsePlansEnabled } = useEnabledFeatures();
    const query = useQuery(
        ['unitsTab', eventId] as const,
        ({ queryKey }) => {
            return apiClient
                .request<UnitsTabResource.GetUnitsTab>({
                    method: 'GET',
                    url: `units_tab/${queryKey[1]}/`,
                })
                .then((data) => {
                    return data.data.data;
                });
        },
        {
            cacheTime: TAB_QUERY_CACHE_TIME,
            enabled: !!eventId,
        }
    );

    /**
     * With async response plans, the recommendation is persisted after the units request returns
     * and announced only over `responsePlanGenerated`, so a fetch made before then shows none.
     * Refetch when a push for this event lands, failed pushes included, so any recorded outcome
     * shows. Delivered plans are committed before their push, so the refetch sees them.
     */
    const { refetch } = query;
    const streamListener = useMemo(() => {
        if (!asyncResponsePlansEnabled || !eventId) {
            return undefined;
        }
        return createStreamListener({
            streamNames: ['responsePlanGenerated'] as const,
            onMessage: (message) => {
                const push: ResponsePlanGeneratedMessage = message.payload;
                if (push.eventId === eventId) {
                    refetch();
                }
            },
        });
    }, [asyncResponsePlansEnabled, eventId, refetch]);
    useStream(streamListener);

    return query;
}
